export const PUBLIC_ARWEAVE_GATEWAY_HOST = 'arweave.net';
export const TURBO_ARWEAVE_GATEWAY_HOST = 'turbo-gateway.com';
export const SOLANA_APP_STORE_ARWEAVE_GATEWAY_HOST = 'solanaappstore.com';
export const SOLANA_APP_STORE_R2_HOST = 'r2.solanaappstore.com';
// Retained only to rewrite old persisted/on-chain URIs.
export const LEGACY_PUBLIC_ARWEAVE_GATEWAY_HOST = 'solanamobiledappstore.com';
export const LEGACY_SOLANA_APP_STORE_R2_HOST = 'r2.solanamobiledappstore.com';
export const DEPRECATED_PUBLIC_ARWEAVE_GATEWAY_HOST = 'dappstorecontent.com';

export const PUBLIC_ARWEAVE_GATEWAY_HOST_ALIASES = [
  LEGACY_PUBLIC_ARWEAVE_GATEWAY_HOST,
  DEPRECATED_PUBLIC_ARWEAVE_GATEWAY_HOST,
] as const;
export const PUBLIC_ARWEAVE_GATEWAY_HOSTS = [
  PUBLIC_ARWEAVE_GATEWAY_HOST,
  TURBO_ARWEAVE_GATEWAY_HOST,
  SOLANA_APP_STORE_ARWEAVE_GATEWAY_HOST,
] as const;
export const DEFAULT_PUBLIC_ARWEAVE_GATEWAY_HOST = PUBLIC_ARWEAVE_GATEWAY_HOST;

type ConfiguredGatewayHosts = string | readonly string[] | null | undefined;

function toSafeUrl(input: string): URL | null {
  try {
    return new URL(input);
  } catch {
    return null;
  }
}

export function canonicalizeSolanaAppStoreUrl(rawUrl: string): string {
  const parsed = toSafeUrl(rawUrl);
  if (!parsed) {
    return rawUrl;
  }

  const hostname = parsed.hostname.toLowerCase();
  if (hostname === LEGACY_PUBLIC_ARWEAVE_GATEWAY_HOST) {
    parsed.hostname = SOLANA_APP_STORE_ARWEAVE_GATEWAY_HOST;
  } else if (hostname === LEGACY_SOLANA_APP_STORE_R2_HOST) {
    parsed.hostname = SOLANA_APP_STORE_R2_HOST;
  } else {
    return rawUrl;
  }

  return parsed.toString();
}

function isHostOrSubdomain(hostname: string, host: string): boolean {
  return hostname === host || hostname.endsWith(`.${host}`);
}

function isCurrentArweaveGatewayHost(hostname: string): boolean {
  return (
    hostname === SOLANA_APP_STORE_ARWEAVE_GATEWAY_HOST ||
    [PUBLIC_ARWEAVE_GATEWAY_HOST, TURBO_ARWEAVE_GATEWAY_HOST].some((host) =>
      isHostOrSubdomain(hostname, host),
    )
  );
}

export function normalizeArweaveGatewayHost(host?: string | null): string {
  const candidate = host?.trim();
  if (!candidate) {
    return DEFAULT_PUBLIC_ARWEAVE_GATEWAY_HOST;
  }

  const parsed = toSafeUrl(
    candidate.includes('://') ? candidate : `https://${candidate}`,
  );
  if (!parsed?.hostname) {
    throw new Error(`Invalid Arweave gateway host: ${candidate}`);
  }

  return parsed.hostname.toLowerCase();
}

function getConfiguredGatewayHosts(
  configuredHosts?: ConfiguredGatewayHosts,
): string[] {
  if (!configuredHosts) {
    return [
      ...PUBLIC_ARWEAVE_GATEWAY_HOSTS,
      ...PUBLIC_ARWEAVE_GATEWAY_HOST_ALIASES,
    ];
  }

  const rawHosts = Array.isArray(configuredHosts)
    ? configuredHosts
    : [configuredHosts];

  return Array.from(
    new Set([
      ...rawHosts
        .filter((host): host is string => typeof host === 'string')
        .map((host) => normalizeArweaveGatewayHost(host)),
      ...PUBLIC_ARWEAVE_GATEWAY_HOSTS,
      ...PUBLIC_ARWEAVE_GATEWAY_HOST_ALIASES,
    ]),
  );
}

function getPrimaryConfiguredGatewayHost(
  configuredHosts?: ConfiguredGatewayHosts,
): string {
  return normalizeArweaveGatewayHost(
    Array.isArray(configuredHosts) ? configuredHosts[0] : configuredHosts,
  );
}

export function isKnownArweaveGatewayHost(
  hostname: string,
  configuredHosts?: ConfiguredGatewayHosts,
): boolean {
  const normalizedHost = hostname.toLowerCase();
  return (
    isCurrentArweaveGatewayHost(normalizedHost) ||
    getConfiguredGatewayHosts(configuredHosts).includes(normalizedHost)
  );
}

export function isArweaveGatewayUrl(
  rawUrl: string,
  configuredHosts?: ConfiguredGatewayHosts,
): boolean {
  const parsed = toSafeUrl(rawUrl);
  if (!parsed) {
    return false;
  }

  return isKnownArweaveGatewayHost(parsed.hostname, configuredHosts);
}

export function buildArweaveGatewayUrl(
  transactionId: string,
  configuredHosts?: ConfiguredGatewayHosts,
): string {
  const host = getPrimaryConfiguredGatewayHost(configuredHosts);
  const path = transactionId.replace(/^\/+/, '');
  return `https://${host}/${path}`;
}

export function buildTurboGatewayUrl(transactionId: string): string {
  return buildArweaveGatewayUrl(transactionId, TURBO_ARWEAVE_GATEWAY_HOST);
}

export function canonicalizeArweaveGatewayUrl(
  rawUrl: string,
  configuredHosts?: ConfiguredGatewayHosts,
): string {
  const canonicalUrl = canonicalizeSolanaAppStoreUrl(rawUrl);
  const parsed = toSafeUrl(canonicalUrl);
  if (!parsed) {
    return rawUrl;
  }

  if (!isKnownArweaveGatewayHost(parsed.hostname, configuredHosts)) {
    return canonicalUrl;
  }

  const normalizedHost = parsed.hostname.toLowerCase();
  if (
    normalizedHost === SOLANA_APP_STORE_ARWEAVE_GATEWAY_HOST ||
    isHostOrSubdomain(normalizedHost, TURBO_ARWEAVE_GATEWAY_HOST) ||
    normalizedHost.endsWith(`.${PUBLIC_ARWEAVE_GATEWAY_HOST}`)
  ) {
    return canonicalUrl;
  }

  parsed.hostname = getPrimaryConfiguredGatewayHost(configuredHosts);
  return parsed.toString();
}

export function toTurboGatewayUrl(
  rawUrl: string,
  configuredHosts?: ConfiguredGatewayHosts,
): string | null {
  const parsed = toSafeUrl(rawUrl);
  if (!parsed) {
    return null;
  }

  if (!isKnownArweaveGatewayHost(parsed.hostname, configuredHosts)) {
    return null;
  }

  if (
    isHostOrSubdomain(parsed.hostname.toLowerCase(), TURBO_ARWEAVE_GATEWAY_HOST)
  ) {
    return rawUrl;
  }

  const normalizedHost = parsed.hostname.toLowerCase();
  parsed.hostname = normalizedHost.endsWith(`.${PUBLIC_ARWEAVE_GATEWAY_HOST}`)
    ? `${normalizedHost.slice(0, -PUBLIC_ARWEAVE_GATEWAY_HOST.length)}${TURBO_ARWEAVE_GATEWAY_HOST}`
    : TURBO_ARWEAVE_GATEWAY_HOST;
  return parsed.toString();
}

export function getArweaveGatewayCandidates(
  rawUrl: string,
  configuredHosts?: ConfiguredGatewayHosts,
): string[] {
  const primaryUrl = canonicalizeArweaveGatewayUrl(rawUrl, configuredHosts);
  const parsedPrimaryUrl = toSafeUrl(primaryUrl);
  const hasContentPath = parsedPrimaryUrl?.pathname.split('/').some(Boolean);
  const isTurboGateway = parsedPrimaryUrl
    ? isHostOrSubdomain(
        parsedPrimaryUrl.hostname.toLowerCase(),
        TURBO_ARWEAVE_GATEWAY_HOST,
      )
    : false;
  const hasTurboSubdomain = parsedPrimaryUrl?.hostname
    .toLowerCase()
    .endsWith(`.${TURBO_ARWEAVE_GATEWAY_HOST}`);
  const hasArweaveSubdomain = parsedPrimaryUrl?.hostname
    .toLowerCase()
    .endsWith(`.${PUBLIC_ARWEAVE_GATEWAY_HOST}`);
  let fallbackUrl: string | null = null;

  if (
    isTurboGateway &&
    (hasContentPath || hasTurboSubdomain) &&
    parsedPrimaryUrl
  ) {
    const normalizedHost = parsedPrimaryUrl.hostname.toLowerCase();
    parsedPrimaryUrl.hostname = hasTurboSubdomain
      ? `${normalizedHost.slice(0, -TURBO_ARWEAVE_GATEWAY_HOST.length)}${PUBLIC_ARWEAVE_GATEWAY_HOST}`
      : PUBLIC_ARWEAVE_GATEWAY_HOST;
    fallbackUrl = parsedPrimaryUrl.toString();
  } else if (hasContentPath || hasArweaveSubdomain) {
    fallbackUrl = toTurboGatewayUrl(primaryUrl, configuredHosts);
  }

  if (!fallbackUrl || fallbackUrl === primaryUrl) {
    return [primaryUrl];
  }

  return [primaryUrl, fallbackUrl];
}

export function extractArweaveTransactionId(
  rawUrl: string,
  configuredHosts?: ConfiguredGatewayHosts,
): string | null {
  const parsed = toSafeUrl(rawUrl);
  if (!parsed) {
    return null;
  }

  if (!isKnownArweaveGatewayHost(parsed.hostname, configuredHosts)) {
    return null;
  }

  const pathSegments = parsed.pathname.split('/').filter(Boolean);
  const transactionId =
    pathSegments[0] === 'raw' ? pathSegments[1] : pathSegments[0];

  return transactionId && /^[A-Za-z0-9_-]{43}$/.test(transactionId)
    ? transactionId
    : null;
}
